import React, { useState } from "react";
import { Form, Input, Button, message } from "antd";
import {
  MailOutlined,
  LockOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";

import "./AuthPages.css";

const { Password } = Input;

const AWS_MARKETPLACE_URL =
  process.env.REACT_APP_AWS_MARKETPLACE_URL ||
  "https://aws.amazon.com/marketplace/pp/prodview-wmywqkxps32au";

const SignIn: React.FC<{ onSignIn?: () => void }> = ({ onSignIn }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const handleSignIn = async (values: any) => {
    try {
      setLoading(true);
      await Auth.signIn(values.email, values.password);

      message.success("Signed in successfully");
      onSignIn?.();
    } catch (error: any) {
      console.error("Sign in error:", error);
      let errMsg = "Failed to sign in";
      if (error.code === "NotAuthorizedException") {
        errMsg = "Incorrect email or password";
      } else if (error.code === "UserNotConfirmedException") {
        let resentOk = false;
        try {
          await Auth.resendSignUp(values.email);
          resentOk = true;
        } catch (resendErr: any) {
          if (resendErr.code === "LimitExceededException") {
            message.warning("Email not verified. Check your inbox for a previous code or resend from the next screen.");
          } else {
            message.warning("Email not verified — we could not resend your code. Use the resend option on the next screen.");
          }
        }
        if (resentOk) {
          message.warning("Email not verified — we resent your code.");
        }
        navigate(`/verify-email?email=${encodeURIComponent(values.email)}`);
        return;
      } else if (error.code === "UserNotFoundException") {
        errMsg = "No account found with this email";
      } else if (error.code === "PasswordResetRequiredException") {
        errMsg = "Password reset required. Please use forgot password.";
        setForgotMode(true);
      } else if (error.message) {
        errMsg = error.message;
      }
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values: any) => {
    try {
      setLoading(true);
      await Auth.forgotPassword(values.email);
      setForgotEmail(values.email);
      setResetMode(true);
      message.success("Verification code sent to your email");
    } catch (error: any) {
      console.error("Forgot password error:", error);
      message.error(error.message || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (values: any) => {
    try {
      setLoading(true);
      await Auth.forgotPasswordSubmit(
        forgotEmail,
        values.code,
        values.newPassword,
      );
      message.success("Password reset successfully! Please sign in.");
      setResetMode(false);
      setForgotMode(false);
      form.resetFields();
    } catch (error: any) {
      console.error("Reset password error:", error);
      message.error(error.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const particles = Array.from({ length: 6 }, (_, i) => (
    <div key={i} className="auth-particle" />
  ));

  // ── Forgot Password: enter email ──
  if (forgotMode && !resetMode) {
    return (
      <div className="auth-page">
        {particles}
        <LeftPanel />
        <div className="auth-right-panel">
          <div className="auth-card">
            <div className="auth-card-header">
              <div className="auth-card-title">Reset Password</div>
              <div className="auth-card-subtitle">
                Enter your email and we'll send a verification code
              </div>
            </div>
            <Form form={form} layout="vertical" onFinish={handleForgotPassword}>
              <div className="auth-field">
                <label className="auth-label">Email Address</label>
                <div className="auth-input-wrap">
                  <MailOutlined className="auth-input-icon" />
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: "Email is required" },
                      { type: "email", message: "Enter a valid email" },
                    ]}
                    >
                    <Input placeholder="you@company.com" />
                  </Form.Item>
                </div>
              </div>
              <Form.Item>
                <Button
                  className="auth-submit-btn"
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  Send Reset Code
                </Button>
              </Form.Item>
            </Form>
            <div className="auth-footer">
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "var(--auth-accent)",
                  fontWeight: 500,
                  fontSize: 14,
                  fontFamily: "inherit",
                }}
                onClick={() => setForgotMode(false)}
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Reset Password: enter code + new password ──
  if (resetMode) {
    return (
      <div className="auth-page">
        {particles}
        <LeftPanel />
        <div className="auth-right-panel">
          <div className="auth-card">
            <div className="auth-card-header">
              <div className="auth-card-title">Enter New Password</div>
              <div className="auth-card-subtitle">
                A code was sent to <strong>{forgotEmail}</strong>
              </div>
            </div>
            <Form form={form} layout="vertical" onFinish={handleResetPassword}>
              <div className="auth-field">
                <label className="auth-label">Verification Code</label>
                <div className="auth-input-wrap">
                  <LockOutlined className="auth-input-icon" />
                  <Form.Item
                    name="code"
                    rules={[{ required: true, message: "Code is required" }]}
                    >
                    <Input placeholder="Enter 6-digit code" />
                  </Form.Item>
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">New Password</label>
                <div className="auth-input-wrap">
                  <LockOutlined className="auth-input-icon" />
                  <Form.Item
                    name="newPassword"
                    rules={[
                      { required: true, message: "Password is required" },
                      { min: 8, message: "Minimum 8 characters" },
                    ]}
                    >
                    <Password placeholder="New password" />
                  </Form.Item>
                </div>
              </div>
              <Form.Item>
                <Button
                  className="auth-submit-btn"
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  Reset Password
                </Button>
              </Form.Item>
            </Form>
            <div className="auth-footer">
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "var(--auth-accent)",
                  fontWeight: 500,
                  fontSize: 14,
                  fontFamily: "inherit",
                }}
                onClick={() => {
                  setResetMode(false);
                  setForgotMode(false);
                }}
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Sign In ──
  return (
    <div className="auth-page">
      {particles}
      <LeftPanel />
      <div className="auth-right-panel">
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-card-title">Welcome back</div>
            <div className="auth-card-subtitle">
              Sign in to continue to your dashboard.{" "}
              <a
                href={AWS_MARKETPLACE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Subscribe here
              </a>
            </div>
          </div>

          <Form form={form} layout="vertical" onFinish={handleSignIn}>
            <div className="auth-field">
              <label className="auth-label">Email Address</label>
              <div className="auth-input-wrap">
                <MailOutlined className="auth-input-icon" />
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input placeholder="you@company.com" />
                </Form.Item>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Password</label>
              <div className="auth-input-wrap">
                <LockOutlined className="auth-input-icon" />
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Password is required" },
                    { min: 8, message: "Minimum 8 characters" },
                  ]}
                >
                  <Password placeholder="Enter your password" />
                </Form.Item>
              </div>
            </div>

            <button
              type="button"
              className="auth-forgot-link"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
              onClick={() => setForgotMode(true)}
            >
              Forgot password?
            </button>

            <Form.Item>
              <Button
                className="auth-submit-btn"
                type="primary"
                htmlType="submit"
                loading={loading}
                block
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>

          <a
            className="auth-help-link"
            href="https://mail.google.com/mail/?view=cm&fs=1&to=aws@complyrim.com&su=Complyrim%20Support%20Request"
            target="_blank"
            rel="noopener noreferrer"
          >
            <QuestionCircleOutlined /> Need help?
          </a>
        </div>
      </div>
    </div>
  );
};

/* ── Left Panel (shared across auth screens) ── */
const LeftPanel: React.FC = () => (
  <div className="auth-left-panel">
    <div className="auth-brand">
      <div className="auth-brand-logo">
        <img
          src="/ControlDesign.png"
          alt="Control Design Pro"
          style={{ width: 44, height: 44, borderRadius: 12 }}
        />
        <div>
          <div className="auth-brand-name">Control Design Pro</div>
          <div className="auth-brand-sub">by Complyrim</div>
        </div>
      </div>
    </div>

    <div className="auth-hero-title">
      Control &amp; Monitoring <br />
      <span className="accent">Design Assessment</span>
    </div>

    <div className="auth-hero-desc">
      AI Assist Control Design Assessment CDA, Monitoring Design Assessment MDA,
      and Control and Monitoring Assessment that reduce manual effort and
      improve audit preparedness across regulated and non regulated industries
    </div>

    <div className="auth-trust-row">
      <div className="auth-trust-item">
        <span className="auth-trust-dot" />
        AWS Marketplace
      </div>
      <div className="auth-trust-item">
        <span className="auth-trust-dot" />
        SOC 2 Ready
      </div>
      <div className="auth-trust-item">
        <span className="auth-trust-dot" />
        AI Powered
      </div>
    </div>

    <div className="auth-status-bar">
      <span className="auth-status-dot" />
      <span>All systems operational</span>
      <span>v2.0</span>
    </div>
  </div>
);

export { LeftPanel };
export default SignIn;
